/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import type React from "react";

import { motion } from "framer-motion";
import { Mail, Clock, Send } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { apiUrl } from "../constants";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const subjectFromURL = params.get("subject");

    if (subjectFromURL && !formData.subject) {
      setFormData((prev) => ({
        ...prev,
        subject: subjectFromURL,
      }));
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent) => {
    setFormData({
      ...formData,
      [(e.target as any).name]: (e.target as any).value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${apiUrl}contact`, {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
      });
      if (response.data) {
        setFormData({
          name: "",
          email: "",
          subject: "",
          message: "",
        });
        alert("Thank you for your message! We will get back to you soon.");
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email Us",
      details: "info@nautilustimes.com",
      description: "",
    },
    {
      icon: Mail,
      title: "Regional Offices",
      details: (
        <div className="space-y-1">
          <div>
            Dubai:{" "}
            <a
              href="mailto:dubai@nautilustimes.com"
              className="text-teal-600 hover:underline"
            >
              dubai@nautilustimes.com
            </a>
          </div>
          <div>
            Thailand:{" "}
            <a
              href="mailto:thailand@nautilustimes.com"
              className="text-teal-600 hover:underline"
            >
              thailand@nautilustimes.com
            </a>
          </div>
          <div>
            India:{" "}
            <a
              href="mailto:india@nautilustimes.com"
              className="text-teal-600 hover:underline"
            >
              india@nautilustimes.com
            </a>
          </div>

          <div>
            Singapore:{" "}
            <a
              href="mailto:singapore@nautilustimes.com"
              className="text-teal-600 hover:underline"
            >
              singapore@nautilustimes.com
            </a>
          </div>

          <div>
            Hong Kong:{" "}
            <a
              href="mailto:hongkong@nautilustimes.com"
              className="text-teal-600 hover:underline"
            >
              hongkong@nautilustimes.com
            </a>
          </div>

          <div>
            Greece:{" "}
            <a
              href="mailto:greece@nautilustimes.com"
              className="text-teal-600 hover:underline"
            >
              greece@nautilustimes.com
            </a>
          </div>
        </div>
      ),
      description: "",
    },
    {
      icon: Mail,
      title: "Training and Admissions",
      details: (
        <div className="space-y-1">
          <div>
            Training:{" "}
            <a
              href="mailto:training@nautilustimes.com"
              className="text-teal-600 hover:underline"
            >
              training@nautilustimes.com
            </a>
          </div>
          <div>
            Admissions:{" "}
            <a
              href="mailto:admissions@nautilustimes.com"
              className="text-teal-600 hover:underline"
            >
              admissions@nautilustimes.com
            </a>
          </div>
        </div>
      ),
      description: "",
    },
    {
      icon: Clock,
      title: "Business Hours",
      details: "Monday - Friday: 9:00 - 17:00",
      description: "Weekend support available",
    },
  ];

  const subjects = [
    "General Inquiry",
    "Course Information",
    "Certification Services",
    "Vessel Registration",
    "Technical Support",
    "Partnership Opportunities",
    "Careers",
  ];

  return (
    <div className="min-h-screen bg-slate-50 pt-20">
      {/* Header */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 py-20 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="mb-6 text-4xl font-bold md:text-6xl">Contact Us</h1>
            <p className="mx-auto max-w-3xl text-xl text-slate-300">
              Get in touch with our maritime experts. We're here to help you
              navigate your maritime career and answer any questions you may
              have.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="rounded-2xl bg-white p-8 shadow-lg"
          >
            <h2 className="mb-6 text-2xl font-bold text-slate-900">
              Send us a Message
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <label
                    htmlFor="name"
                    className="mb-2 block text-sm font-medium text-slate-700"
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter your full name"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="mb-2 block text-sm font-medium text-slate-700"
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-teal-500"
                    placeholder="Enter your email"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="subject"
                  className="mb-2 block text-sm font-medium text-slate-700"
                >
                  Subject *
                </label>
                <select
                  id="subject"
                  name="subject"
                  required
                  value={formData.subject}
                  onChange={handleInputChange}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Select a subject</option>
                  {subjects.map((subject) => (
                    <option key={subject} value={subject}>
                      {subject}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="mb-2 block text-sm font-medium text-slate-700"
                >
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  value={formData.message}
                  onChange={handleInputChange}
                  className="w-full resize-none rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-teal-500"
                  placeholder="Tell us how we can help you..."
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="flex w-full items-center justify-center space-x-2 rounded-xl bg-teal-600 px-6 py-4 font-semibold text-white transition-colors duration-200 hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                ) : (
                  <>
                    <Send size={20} />
                    <span>Send Message</span>
                  </>
                )}
              </button>
            </form>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            <div className="rounded-2xl bg-white p-8 shadow-lg">
              <h2 className="mb-6 text-2xl font-bold text-slate-900">
                Get in Touch
              </h2>
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-teal-100">
                      <info.icon className="h-6 w-6 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="mb-1 font-semibold text-slate-900">
                        {info.title}
                      </h3>
                      <p className="mb-1 text-slate-700">{info.details}</p>
                      <p className="text-sm text-slate-500">
                        {info.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
